import React, { useState, useEffect, useRef } from 'react'
import {
  Table,
  Card,
  Col,
  Row,
  Button,
  Input,
  Select,
  DatePicker,
  Switch,
  Space,
  Typography,
  Tag,
  Pagination,
  message,
  Modal,
} from 'antd'
import {
  SearchOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  EditOutlined,
  PlusOutlined,
  TrophyOutlined,
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import moment from 'moment'
import { getMarketplace, bulkEdit } from 'src/api/marketplace'
import { fetchCountryProvider } from 'src/api/authen'
import { manualFetch } from 'src/utils/fetch'
import './Marketplaceindex.css'
import { set } from 'ramda'
const { Title, Text } = Typography
const { RangePicker } = DatePicker

interface AsyncImageProps {
  src: string
  alt: string
  className?: string
  style?: React.CSSProperties
}

interface MarketplaceItem {
  id: string
  images: string[]
  originalImages?: string[]
  name: string
  nameFr?: string
  desc?: string
  descFr?: string
  subTitle?: string
  subTitleFr?: string
  category: string
  points: number
  price?: number
  providerCountry: string
  enabled: boolean
}

const AsyncImage: React.FC<AsyncImageProps> = ({
  src,
  alt,
  className = '',
  style,
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!src) return

    let revokedUrl: string | null = null

    const fetchImg = async () => {
      
      try {
        const response = await manualFetch(src, {
          headers: {
            Accept: 'image/*',
          },
        })

        const imageBlob = await response.blob()
        const objectUrl = URL.createObjectURL(imageBlob)
        revokedUrl = objectUrl
        setImageUrl(objectUrl)
      } catch (err) {
        console.error('Image load failed:', err)
      }
    }

    fetchImg()

    return () => {
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl)
      }
    }
  }, [src])

  return imageUrl ? (
    <img src={imageUrl} alt={alt} className={className} style={style} />
  ) : (
    <div
      style={{
        ...style,
        background: '#1f1f1f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text type="secondary">Loading...</Text>
    </div>
  )
}

interface MarketplaceItem {
  id: string
  images: string[]
  name: string
  category: string
  points: number
  createdOn: string
  providerCountry: string
  enabled: boolean
  imageUrlArray
  description: string
  descriptionFr: string
}

export const Marketplace = () => {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<MarketplaceItem[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [countries, setCountries] = useState([])
  const [selectedCountryValue, setSelectedCountryValue] = useState<
    string | undefined
  >(null)
  const shouldRefetchRef = useRef(false)
  const [bulkAction, setBulkAction] = useState(undefined)

  const [searchTerm, setSearchTerm] = useState('')
  const [status, setStatus] = useState(null)
 
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const navigate = useNavigate()
  interface Filters {
    page: number
    size: number
    search?: string
    country?: string
    status?: boolean
    startDate?: Dayjs
    endDate?: Dayjs
  }

  const filtersRef = useRef<Filters>({
    page: 0,
    size: 10,

    country: undefined,
    status: null,
    startDate:null ,
    endDate: dayjs(),
  })

  const fetchCountry = async () => {
    try {
      const data = await fetchCountryProvider()
      const countryOptions = [
        { label: 'All', value: null },
        ...data.data.map((item) => ({
          label: item.countryName,
          value: item.countryCode,
        })),
      ]
      setCountries(countryOptions)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleAddMarketPlace = () => {
    navigate('/marketplace/add')
  }
  const fetchImage = async (url) => {
    const response = await manualFetch(url, {
      headers: {
        accept: 'image/*',
      },
    })
    const imageBlob = await response.blob()
    return URL.createObjectURL(imageBlob)
  }

  const handleMonthlyMarketPlace = () => {
    navigate('/marketplace/monthly')
  }

  const navigateToEdit = (record: MarketplaceItem) => {
    const params = new URLSearchParams()
    params.append('id', record.id)
    params.append('img', record.imageUrlArray || '')
    params.append('name', record.name || '')
    params.append('nameFr', record.nameFr || '')
    params.append('desc', record.description || '')
    params.append('descFr', record.descriptionFr || '')
    params.append('enabled', String(record.enabled))
    params.append('points', String(record.points))
    params.append('price', String(record.price ?? ''))
    params.append('subTitle', record.subTitle || '')
    params.append('subTitleFr', record.subTitleFr || '')
    params.append('category', record.category || '')
    params.append('originalImages', (record.images || []).join(','))
    params.append('country', record.providerCountry || '')

    navigate(`/marketplace/edit/${record.id}?${params.toString()}`)
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const { page, size, status } = filtersRef.current
      const response = await getMarketplace(
        page,
        size,
        startDate ? startDate.format('YYYY-MM-DD') : undefined,
        endDate ? endDate.format('YYYY-MM-DD') : undefined,
        status,
        selectedCountryValue,
        filtersRef.current.search
      )

      const filteredData = response?.data || []

      const marketplaceData = await Promise.all(
        filteredData.map(async (element) => {
          const imageUrlArray = await Promise.all(
            element.images.map((image) => fetchImage(image))
          )
          return {
            ...element,
            imageUrlArray,
          }
        })
      )
      if (marketplaceData.length === 0 && page > 0) {
        filtersRef.current.page = page - 1
        shouldRefetchRef.current = true
        return
      }

      setData(marketplaceData)
      setTotalItems(response?.total_items || filteredData.length)
    } catch (error) {
      console.error('Failed to fetch data', error)
      message.error('Failed to fetch data')
    } finally {
      setLoading(false)
    }
  }
  const _countryChange = (value: string) => {
    setSelectedCountryValue(value)
  }

  const handleBulkEdit = async (enabled: boolean) => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select items to edit')
      return
    }

    try {
      await bulkEdit(selectedRowKeys, enabled)
      message.success(`Updated ${selectedRowKeys.length} items`)
      setSelectedRowKeys([])
      fetchData()
    } catch (error) {
      console.error('Bulk edit failed', error)
      message.error('Bulk edit failed')
    }
  }

  const handleSearch = () => {
    filtersRef.current.page = 0
    filtersRef.current.search = searchTerm
    fetchData()
  }

  const confirmBulkEdit = (enabled: boolean) => {
    const actionText = enabled ? 'Enable' : 'Disable'

    Modal.confirm({
      title: `Confirm ${actionText}`,
      content: (
        <>
          <p>
            Are you sure you want to <b>{actionText.toLowerCase()}</b>{' '}
            <b>{selectedRowKeys.length}</b> selected item(s)?
          </p>
        </>
      ),
      okText: actionText,
      okButtonProps: {
        danger: !enabled,
      },
      cancelText: 'Cancel',
      onOk: async () => {
        await handleBulkEdit(enabled)
      },
    })
  }

  const columns = [
    {
      title: 'Image',
      dataIndex: 'imageUrlArray',
      render: (images: string[]) => (
        <AsyncImage
          src={images?.[0] || ''}
          alt="product"
          style={{ width: 40, height: 40, borderRadius: 4 }}
        />
      ),
    },
    { title: 'Product Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Category',
      dataIndex: 'category',
      render: (cat: string) => <Tag color="purple">{cat || 'N/A'}</Tag>,
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      render: (points: number) => <Text strong>{points}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'createdOn',
      render: (ts: string) => dayjs(ts).format('YYYY-MM-DD'),
    },
    {
      title: 'Country',
      dataIndex: 'providerCountry',
      render: (country: string) => {
        const countryObj = countries.find((c) => c.value === country)
        return countryObj ? countryObj.label : country
      },
    },
    {
      title: 'Published',
      dataIndex: 'enabled',
      render: (val: boolean, record: MarketplaceItem) => (
        <Switch
          checked={val}
          size="small"
          style={{ backgroundColor: '#683cca' }}
          onChange={async (checked: boolean) => {
            setData((prev) =>
              prev.map((row) =>
                row.id === record.id ? { ...row, enabled: checked } : row
              )
            )
            try {
              await bulkEdit([record.id], checked)
              message.success('Status updated')
              fetchData()
            } catch (error) {
              setData((prev) =>
                prev.map((row) =>
                  row.id === record.id ? { ...row, enabled: !checked } : row
                )
              )
              message.error('Failed to update status')
            }
          }}
        />
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: MarketplaceItem) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined style={{ color: 'white' }} />}
            className="btn-primary"
            onClick={() => {
              navigateToEdit(record)
            }}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]

  useEffect(() => {
    
    fetchData()
  }, [selectedCountryValue, status, startDate, endDate])

  useEffect(() => {
    if (shouldRefetchRef.current) {
      shouldRefetchRef.current = false
      fetchData()
    }
  }, [filtersRef.current.page])

  

  useEffect(() => {
   
    fetchCountry()
  
  }, [])

  return (
    <div style={{ padding: '24px', background: '#000', minHeight: '100vh' }}>
      <Title level={4} style={{ color: '#fff', marginBottom: 24 }}>
        Marketplace
      </Title>

      <Row gutter={10}>
        <Col span={4}>
          <Input
            placeholder="Search Product"
            onChange={(e) => {
              setSearchTerm(e.target.value)
            }}
            style={{ borderRadius: 5 }}
            onPressEnter={handleSearch}
            allowClear
          />
        </Col>
        <Col span={4}>
          <Select
            value={selectedCountryValue}
            style={{ width: '100%', borderRadius: 5 }}
            onChange={_countryChange}
            options={countries}
            loading={loading}
          >
            {countries.map((country) => (
              <Select.Option value={country.value} key={country.value}>
                {country.label}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={3}>
          <Select
            
           value={status}
            style={{ width: '100%', borderRadius: 5 }}
            onChange={(val: boolean) => {
              filtersRef.current.status = val
              setStatus(val)
            }}
            options={[
              { label: 'All', value: null },
              { label: 'Active', value: true },
              { label: 'Inactive', value: false },
            ]}
          />
        </Col>
        <Col span={9}>
          <DatePicker.RangePicker
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
            value={[startDate, endDate]}
            allowClear
            
            onChange={(dates) => {
              const newStart = dates?.[0] || null
              const newEnd = dates?.[1] || null
              setStartDate(newStart)
              setEndDate(newEnd)
            }}
          />
        </Col>
        <Col span={4}>
          <Button
            type="primary"
            className="btn-primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
            style={{ borderRadius: 5 }}
          >
            Search
          </Button>
        </Col>
      </Row>

      <Row justify="space-between" align="middle" style={{ marginTop: 20 }}>
        <Col>
          <Space>
            <Button
              icon={<UnorderedListOutlined />}
              type={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
              style={{
                backgroundColor: 'black',
                color: 'white',
                borderRadius: 5,
                width: 35,
              }}
            />
            <Button
              icon={<AppstoreOutlined />}
              type={viewMode === 'grid' ? 'primary' : 'default'}
              onClick={() => setViewMode('grid')}
              style={{
                backgroundColor: 'black',
                color: 'white',
                borderRadius: 5,
                width: 35,
              }}
            />
            {viewMode === 'list' && (
              <Select
                placeholder="Bulk Action"
                style={{ width: 160, borderRadius: 5 }}
                disabled={selectedRowKeys.length === 0}
                value={bulkAction}
                onSelect={(value) => {
                  confirmBulkEdit(value)
                  setBulkAction(undefined)
                }}
                options={[
                  {
                    label: 'Enable',
                    value: true,
                    disabled: status === true,
                  },
                  {
                    label: 'Disable',
                    value: false,
                    disabled: status === false,
                  },
                ]}
              />
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<TrophyOutlined />}
              style={{
                backgroundColor: '#6366f1',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
              }}
              onClick={handleMonthlyMarketPlace}
            >
              Set Monthly Prizes
            </Button>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              style={{
                backgroundColor: '#0ea5e9',
                border: 'none',
                borderRadius: 5,
              }}
              onClick={handleAddMarketPlace}
            >
              Add Marketplace
            </Button>
          </Space>
        </Col>
      </Row>

      {viewMode === 'list' ? (
        <div
          style={{
            background: '#141414',
            marginTop: 20,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <Table
            loading={loading}
            dataSource={data}
            columns={columns}
            rowKey="id"
            pagination={false}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
              preserveSelectedRowKeys: true,
            }}
            style={{ background: '#141414' }}
          />
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            {data.map((item: MarketplaceItem) => (
              <Col xs={24} sm={12} md={8} lg={6} key={item.id}>
                <Card
                  hoverable
                  style={{ background: '#141414', borderColor: '#333' }}
                  cover={
                    <AsyncImage
                      src={item.images?.[0] || ''}
                      alt={item.name}
                      style={{ height: 200, objectFit: 'contain', padding: 10 }}
                    />
                  }
                  bodyStyle={{ padding: '12px' }}
                >
                  <div style={{ marginBottom: 12, height: 60 }}>
                    <Text strong style={{ color: '#fff', fontSize: '14px' }}>
                      {item.name}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Tag color="purple">{item.category}</Tag>
                      <Text style={{ color: '#a855f7', marginLeft: 8 }}>
                        {item.points} pts
                      </Text>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Space>
                      <Button
                        size="small"
                        icon={<EditOutlined style={{ color: 'white' }} />}
                        className="btn-primary"
                        onClick={() => {
                          navigateToEdit(item)
                        }}
                      >
                        Edit
                      </Button>
                    </Space>
                    <Switch
                      checked={item.enabled}
                      style={{ backgroundColor: '#683cca' }}
                      size="small"
                      onChange={async (checked: boolean) => {
                        setData((prev) =>
                          prev.map((row) =>
                            row.id === item.id
                              ? { ...row, enabled: checked }
                              : row
                          )
                        )

                        try {
                          await bulkEdit([item.id], checked)
                          message.success('Status updated')
                          fetchData()
                        } catch (error) {
                          setData((prev) =>
                            prev.map((row) =>
                              row.id === item.id
                                ? { ...row, enabled: !checked }
                                : row
                            )
                          )
                          message.error('Failed to update status')
                        }
                      }}
                    />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Pagination
          current={filtersRef.current.page + 1}
          total={totalItems}
          pageSize={filtersRef.current.size}
          onChange={(page: number) => {
            filtersRef.current.page = page - 1
            fetchData()
          }}
          showSizeChanger
          onShowSizeChange={(_: number, size: number) => {
            filtersRef.current.size = size
            filtersRef.current.page = 0
            fetchData()
          }}
          showTotal={(total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} items`
          }
        />
      </div>
    </div>
  )
}
export default Marketplace
